import { useEffect, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { eventStatusStore } from '~/lib/stores/eventStatus';
import type { EventState } from '~/lib/stores/eventStatus';
import { chatStore } from '~/lib/stores/chat';

/**
 * Hook to synchronize event status store with IndexedDB
 *
 * This hook watches the eventStatusStore and updates IndexedDB accordingly:
 * - PENDING events are upserted to IndexedDB
 * - PROCESSED events are deleted from IndexedDB
 * - FAILED events remain in the store but are not synced to IndexedDB
 *
 * This is separated from the store to avoid circular dependencies between
 * eventStatus.ts and persistence/db.ts
 */
export function useEventStatusSync(enabled: boolean = true) {
  const events = useStore(eventStatusStore);
  const previousEventsRef = useRef<Record<string, EventState>>({});

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }

    // Process changes
    const processChanges = async () => {
      const previousEvents = previousEventsRef.current;
      const currentEvents = events;

      // Find new or updated events
      for (const [eventId, currentState] of Object.entries(currentEvents)) {
        const previousState = previousEvents[eventId];

        // Skip if state hasn't changed
        if (previousState?.status === currentState.status) {
          continue;
        }

        try {
          // Dynamically import to avoid bundling issues
          const { upsertEvent, deleteEvents, getFailedEventsFromDB } = await import('~/lib/persistence/db');
          const { db } = await import('~/lib/persistence');

          if (!db) {
            console.warn('[useEventStatusSync] IndexedDB not available');
            continue;
          }

          // Handle based on status
          if (currentState.status === 'PENDING') {
            console.log('[useEventStatusSync] Upserting PENDING event to IndexedDB:', eventId);
            await upsertEvent(db, { eventId, enqueued: true });

            const failedEvents = await getFailedEventsFromDB(db, currentState.projectId);

            if (failedEvents.length > 0) {
              chatStore.setKey('enqueued', false);
            } else {
              chatStore.setKey('enqueued', true);
            }
          } else if (currentState.status === 'PROCESSED') {
            console.log('[useEventStatusSync] Deleting PROCESSED event from IndexedDB:', eventId);
            await deleteEvents(db, [eventId]);

            const failedEvents = await getFailedEventsFromDB(db, currentState.projectId);

            if (failedEvents.length > 0) {
              chatStore.setKey('enqueued', false);
            } else {
              chatStore.setKey('enqueued', true);
            }
          }

          // Note: FAILED events are kept in the store but not synced to IndexedDB
        } catch (error) {
          console.error('[useEventStatusSync] Error syncing event to IndexedDB:', eventId, error);
        }
      }

      // Update the reference for next comparison
      previousEventsRef.current = { ...currentEvents };
    };

    // Use setTimeout to defer execution and avoid blocking
    const timeoutId = setTimeout(() => {
      processChanges().catch((error) => {
        console.error('[useEventStatusSync] Error processing changes:', error);
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [events, enabled]);
}
